import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Shield, ExternalLink, CheckCircle2 } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';
import { Footer } from '../components/marketing';

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    API.get('/api/tiers').then(r => setTiers(r.data.tiers?.filter(t => t.is_public) || [])).catch(() => {});
  }, []);

  const handleSubscribe = async (tierId) => {
    if (!user) { navigate('/signup'); return; }
    const interval = annual ? 'annual' : 'monthly';
    const packageId = `${tierId}_${interval}`;
    try {
      const res = await API.post('/api/billing/checkout', { package_id: packageId, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (err) {
      console.error('Checkout failed:', err);
    }
  };

  const TIER_COLORS = { free: 'rgba(245,241,232,0.48)', student: '#00D4FF', pro: '#FF4D00', team: '#F2A900' };
  const TIER_FEATURES = {
    free: ['3 voices max', '2 phases max', '5 frays/month', 'Economy models only', 'Markdown export'],
    student: ['5 voices', '3 phases', '30 frays/month', 'Economy models', 'PDF + Markdown export', 'Committee presets', 'Education output mode'],
    pro: ['7 voices', '5 phases', 'Unlimited frays', 'All model tiers', 'Research tools', 'Playbook forking', 'Model comparison', 'BYOK support'],
    team: ['Everything in Pro', 'Per-seat pricing', 'Workspace + departments', 'Team usage dashboard', 'Bulk CSV invite', 'Model tier policies', 'API access'],
  };

  const ENTERPRISE_FEATURES = [
    'Everything in Team',
    'Minimum 10 seats',
    'SSO/SAML integration',
    'SCIM user provisioning',
    'Custom data residency',
    'Dedicated support channel',
    'Audit log explorer',
    'Custom model governance',
    'DPA included',
    'Volume pricing (50+ seats)',
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <div className="pt-24 pb-16 px-4 md:pt-32 md:px-6 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-display-lg font-display text-[#F5F1E8] uppercase mb-3" data-testid="pricing-title">Plans & Pricing</h1>
          <p className="text-[rgba(245,241,232,0.72)] text-base font-body">Start free. Scale as you need.</p>
          <div className="flex items-center justify-center gap-3 mt-6">
            <span className={`text-sm font-mono ${!annual ? 'text-[#F5F1E8]' : 'text-[rgba(245,241,232,0.48)]'}`}>Monthly</span>
            <button onClick={() => setAnnual(!annual)}
              className={`w-12 h-6 rounded-full transition-colors ${annual ? 'bg-[#FF4D00]' : 'bg-[#1E1E1E]'}`} data-testid="annual-toggle">
              <div className={`w-5 h-5 bg-[#F5F1E8] rounded-full transition-transform ${annual ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
            <span className={`text-sm font-mono ${annual ? 'text-[#F5F1E8]' : 'text-[rgba(245,241,232,0.48)]'}`}>Annual <span className="text-[#22C55E] text-xs">Save 20%</span></span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers.map((tier, i) => {
            const color = TIER_COLORS[tier.slug] || 'rgba(245,241,232,0.48)';
            const features = TIER_FEATURES[tier.slug] || [];
            const price = annual ? tier.price_annual : tier.price_monthly;
            const isCurrentPlan = user?.plan === tier.slug;

            return (
              <motion.div key={tier._id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                className={`p-6 bg-[#1E1E1E] border ${isCurrentPlan ? 'border-[#FF4D00]' : tier.slug === 'pro' ? 'border-[#FF4D00]/40' : 'border-[rgba(245,241,232,0.08)]'}`}>
                {tier.slug === 'pro' && <span className="text-[9px] px-2 py-0.5 bg-[#FF4D00] text-[#0A0A0A] font-mono font-bold mb-3 inline-block tracking-wider">MOST POPULAR</span>}
                <h3 className="text-lg font-display font-bold uppercase tracking-tight mb-1" style={{ color }}>{tier.name}</h3>
                <div className="mb-4">
                  {price === 0 || price === null ? (
                    <span className="text-display-sm font-display text-[#F5F1E8]">{tier.slug === 'enterprise' ? 'CUSTOM' : 'FREE'}</span>
                  ) : (
                    <><span className="text-display-sm font-display text-[#F5F1E8]">${annual ? Math.round(price / 12) : price}</span><span className="text-[rgba(245,241,232,0.48)] text-sm font-mono">/mo{tier.per_seat ? '/seat' : ''}</span></>
                  )}
                </div>
                <ul className="space-y-2 mb-6">
                  {features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color }} />
                      <span className="text-[rgba(245,241,232,0.72)] text-xs">{f}</span>
                    </li>
                  ))}
                </ul>
                {isCurrentPlan ? (
                  <span className="block text-center py-2.5 border border-[#FF4D00]/30 text-[#FF4D00] text-xs font-mono tracking-wider">CURRENT PLAN</span>
                ) : tier.slug === 'free' ? (
                  <Link to="/signup" className="block text-center py-2.5 border border-[rgba(245,241,232,0.08)] text-xs font-mono text-[rgba(245,241,232,0.72)] hover:text-[#F5F1E8] hover:border-[rgba(245,241,232,0.16)] transition-all tracking-wider">GET STARTED</Link>
                ) : (
                  <button onClick={() => handleSubscribe(tier.slug)}
                    className="w-full py-2.5 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] text-xs font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#FF6B33] transition-colors" data-testid={`subscribe-${tier.slug}`}>
                    Subscribe <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </motion.div>
            );
          })}

          {/* Enterprise Tier — always shown */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="p-6 bg-[#1E1E1E] border border-[#00D4FF]/20 relative overflow-hidden">
            <div className="h-[2px] w-full bg-[#00D4FF] absolute top-0 left-0" />
            <Shield className="w-5 h-5 text-[#00D4FF] mb-2" />
            <h3 className="text-lg font-display font-bold text-[#00D4FF] uppercase tracking-tight mb-1">Enterprise</h3>
            <div className="mb-4">
              <span className="text-display-sm font-display text-[#F5F1E8]">CUSTOM</span>
            </div>
            <ul className="space-y-2 mb-6">
              {ENTERPRISE_FEATURES.map((f, fi) => (
                <li key={fi} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#00D4FF]" />
                  <span className="text-[rgba(245,241,232,0.72)] text-xs">{f}</span>
                </li>
              ))}
            </ul>
            <a
              href="https://calendly.com/fray-ai"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-2.5 min-h-[44px] border border-[#00D4FF]/30 text-[#00D4FF] text-xs font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#00D4FF]/10 transition-colors"
            >
              Talk to Our Team <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <div className="mt-3 text-center">
              <Link to="/security" className="text-[10px] font-mono text-[rgba(245,241,232,0.48)] hover:text-[#00D4FF] transition-colors">
                View security & compliance →
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Enterprise note */}
        <div className="mt-8 p-4 bg-[#1E1E1E]/50 border border-[rgba(245,241,232,0.08)] text-center">
          <p className="text-xs text-[rgba(245,241,232,0.48)] font-mono">
            Enterprise: 10 seat minimum · Annual contract required · 50+ seats qualify for volume pricing · <Link to="/security" className="text-[#00D4FF] hover:underline">DPA available</Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
